import { formatBR, formatBRDateTime } from '@control-center/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DownloadCloud, RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { PageHelp } from '../../components/common/PageHelp';
import { fetchBootstrapRuns, type BootstrapRun } from '../../lib/bootstrap-api';
import {
  diagnoseSaasConnection,
  isSaasConnected,
  runSaasHealthCheck,
  runSaasImport,
} from '../../lib/saas-products-api';
import { extractErrorMessage } from '../../lib/errors';
import { useSaasProducts } from '../../hooks/use-saas-products';
import { useAuthStore } from '../../stores/useAuthStore';

/**
 * Lista de SaaS products com status de integração runtime.
 * Conectado = config OK + último /health respondeu OK nos últimos 10min.
 * Bootstrap = histórico de catch-up; é seção separada.
 */
export function IntegrationsPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const qc = useQueryClient();
  const saas = useSaasProducts({ limit: 100 });
  const runs = useQuery({
    queryKey: ['bootstrap-runs', 'all'],
    queryFn: () => fetchBootstrapRuns(),
  });

  const [checkingId, setCheckingId] = useState<string | null>(null);
  const checkMutation = useMutation({
    mutationFn: (id: string) => runSaasHealthCheck(id),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['saas-products'] });
      if (result.healthy) {
        toast.success(`Conectado · ${result.responseTimeMs ?? '?'}ms`);
      } else {
        toast.error(`Falhou: ${result.errorMessage ?? `HTTP ${result.httpStatus}`}`);
      }
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Erro ao verificar conexão'));
    },
    onSettled: () => setCheckingId(null),
  });

  const [importingId, setImportingId] = useState<string | null>(null);
  const importMutation = useMutation({
    mutationFn: (id: string) => runSaasImport(id),
    onSuccess: (report) => {
      qc.invalidateQueries({ queryKey: ['saas-products'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      const parts = [
        `${report.usersReceived} usuários recebidos`,
        `${report.clientsCreated} clientes novos`,
        `${report.clientsLinked} vinculados a existentes`,
        `${report.subscriptionsCreated} assinaturas criadas`,
      ];
      toast.success(`Importação ${report.saasSlug} concluída: ${parts.join(' · ')}`, {
        duration: 8000,
      });
      if (report.errors.length > 0) {
        toast.warning(`${report.errors.length} aviso(s): ${report.errors[0]}`, {
          duration: 10000,
        });
      }
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Falha na importação'));
    },
    onSettled: () => setImportingId(null),
  });

  const lastRunBySaas = new Map<string, BootstrapRun>();
  for (const run of runs.data ?? []) {
    if (!lastRunBySaas.has(run.saasProductId)) lastRunBySaas.set(run.saasProductId, run);
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-5 px-6 py-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Integrações SaaS</h1>
          <p className="mt-1 text-sm text-muted">
            Veja quais SaaS estão conectados ao Control Center.
          </p>
        </div>
        <PageHelp id="integrations" />
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(saas.data?.items ?? []).map((s) => {
          const lastRun = lastRunBySaas.get(s.id);
          const connected = isSaasConnected(s);
          const diagnosis = diagnoseSaasConnection(s);
          return (
            <section
              key={s.id}
              className="flex flex-col gap-2 rounded-[12px] border border-line bg-panel p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[14px] font-semibold text-ink">{s.name}</div>
                  <code className="text-[11px] text-muted-2">{s.slug}</code>
                </div>
                <span
                  className={
                    connected
                      ? 'inline-flex items-center gap-[6px] rounded-[6px] border border-good/30 bg-good/10 px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider text-good'
                      : 'inline-flex items-center gap-[6px] rounded-[6px] border border-line bg-panel-2 px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider text-muted'
                  }
                >
                  <span
                    aria-hidden
                    className={
                      connected
                        ? 'h-[7px] w-[7px] rounded-full bg-good ring-[3px] ring-good/15'
                        : 'h-[7px] w-[7px] rounded-full bg-muted-2 ring-[3px] ring-muted-3/15'
                    }
                  />
                  {connected ? 'Conectado' : 'Desconectado'}
                </span>
              </div>

              <DiagnosisLine
                diagnosis={diagnosis}
                checkedAt={s.lastHealthCheck?.checkedAt ?? null}
                responseTimeMs={s.lastHealthCheck?.responseTimeMs ?? null}
              />

              <div className="mt-1 flex items-center justify-between gap-2 border-t border-line pt-2">
                <span className="text-[11px] text-muted-2">
                  {lastRun
                    ? `Última importação: ${lastRun.status} · ${formatBR(lastRun.initiatedAt)}`
                    : 'Nunca importou dados históricos'}
                </span>
                <div className="flex items-center gap-1">
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        setImportingId(s.id);
                        importMutation.mutate(s.id);
                      }}
                      disabled={importingId === s.id || !connected}
                      title={
                        connected
                          ? 'Espelha todos os usuários, empresas e assinaturas desse SaaS pro Control Center'
                          : 'Conecte o SaaS primeiro pra poder importar'
                      }
                      className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-good/30 bg-good/10 px-2 text-[11px] font-medium text-good hover:bg-good/20 disabled:opacity-50"
                    >
                      <DownloadCloud
                        size={11}
                        strokeWidth={1.8}
                        className={importingId === s.id ? 'animate-pulse' : ''}
                      />
                      {importingId === s.id ? 'Importando…' : 'Importar dados'}
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        setCheckingId(s.id);
                        checkMutation.mutate(s.id);
                      }}
                      disabled={checkingId === s.id}
                      title="Verifica a conexão agora (sem esperar o cron de 5min)"
                      className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-line bg-panel-2 px-2 text-[11px] text-ink hover:bg-panel-3 disabled:opacity-50"
                    >
                      <RefreshCcw
                        size={11}
                        strokeWidth={1.8}
                        className={checkingId === s.id ? 'animate-spin' : ''}
                      />
                      Verificar agora
                    </button>
                  )}
                  <Link
                    to={
                      lastRun
                        ? `/integrations/runs/${lastRun.id}`
                        : `/integrations/connect/${s.slug}`
                    }
                    className="inline-flex h-7 items-center rounded-[6px] border border-line bg-panel-2 px-2 text-[11px] text-ink hover:bg-panel-3"
                  >
                    {lastRun ? 'Ver detalhes' : 'Importar histórico'}
                  </Link>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function DiagnosisLine({
  diagnosis,
  checkedAt,
  responseTimeMs,
}: {
  diagnosis: ReturnType<typeof diagnoseSaasConnection>;
  checkedAt: string | null;
  responseTimeMs: number | null;
}): JSX.Element {
  switch (diagnosis.status) {
    case 'connected':
      return (
        <div className="text-[11px] text-muted-2">
          Respondeu em {responseTimeMs ?? '?'}ms · última checagem{' '}
          {checkedAt ? formatBRDateTime(new Date(checkedAt)) : '—'}
        </div>
      );
    case 'no_config':
      return <div className="text-[11px] text-warn">{diagnosis.reasons.join(' · ')}</div>;
    case 'never_checked':
      return (
        <div className="text-[11px] text-muted-2">
          Aguardando primeira verificação (cron roda a cada 5min)
        </div>
      );
    case 'unhealthy':
      return (
        <div className="text-[11px] text-warn">
          SaaS respondeu com erro: {diagnosis.errorMessage ?? `HTTP ${diagnosis.httpStatus ?? '?'}`}
        </div>
      );
    case 'stale': {
      const min = Math.round(diagnosis.ageMs / 60000);
      return (
        <div className="text-[11px] text-warn">
          Sem resposta há {min}min — verifique se o SaaS está no ar
        </div>
      );
    }
  }
}
